import moment from 'moment';

/**
 * Transform an inquiry object from the API response to the shape expected by UI components.
 * @param {object} inquiry - Raw inquiry from API
 * @param {object} options - Optional lookup tables
 * @param {Array} options.stores - Store list from auth.stores for storeId → name mapping
 * @param {Array} options.tags - Merchant tags for tagId → name mapping (fallback only)
 */
export const transformInquiry = (inquiry, { stores = [], tags = [] } = {}) => {
  const inquiryDate = inquiry.inquiryDate
    ? moment(String(inquiry.inquiryDate), 'YYYYMMDD').format('MM/DD/YYYY')
    : '';

  const rawDurationMonths = inquiry.inquiryDate
    ? moment().diff(moment(String(inquiry.inquiryDate), 'YYYYMMDD'), 'months')
    : 0;
  const durationMonths = rawDurationMonths === 0 ? 'This month' : `${rawDurationMonths} months`;

  const storeName = stores.find(s => s.id === inquiry.storeId)?.name || '';

  // Tags: resolve to [{name, color}] for colored pill rendering
  // API now includes color directly on tag objects: [{tagId, tagName, color}]
  let resolvedTags;
  if (inquiry.tags && inquiry.tags.length > 0 && typeof inquiry.tags[0] === 'object') {
    resolvedTags = inquiry.tags
      .map(t => ({
        name: t.tagName || t.name,
        color: t.color || tags.find(mt => mt.id === t.tagId)?.color || '#818181',
      }))
      .filter(t => t.name);
  } else {
    resolvedTags = (inquiry.tagIds || [])
      .map(id => {
        const lookup = tags.find(t => t.id === id);
        return lookup ? { name: lookup.name, color: lookup.color || '#818181' } : null;
      })
      .filter(Boolean);
  }

  // Product info from requestedProducts: [{vendorItemId, mpn, collectionName, image}]
  const firstProduct = inquiry.requestedProducts?.[0];

  // Last inbound date formatting
  const lastInboundDate = inquiry.lastInboundDate;
  const lastInbound = lastInboundDate ? moment(lastInboundDate).format('MM/DD/YYYY') : '';
  let lastInboundDuration = '';
  if (lastInboundDate) {
    const months = moment().diff(moment(lastInboundDate), 'months');
    if (months === 0) lastInboundDuration = 'This month';
    else if (months === 1) lastInboundDuration = '1 month ago';
    else lastInboundDuration = `${months} months ago`;
  }

  return {
    ...inquiry,
    inquiryDate,
    inquiryDateRaw: inquiry.inquiryDate,
    durationMonths,
    associateName: inquiry.associateName || '',
    createdByName: inquiry.createdByName || '',
    storeName,
    tags: resolvedTags,
    productName: firstProduct?.collectionName || '',
    productMpn: firstProduct?.mpn || '',
    productImage: firstProduct?.image || null,
    productVendorItemId: firstProduct?.vendorItemId || null,
    productCount: inquiry.requestedProducts?.length || 0,
    lastInbound,
    lastInboundDuration,
  };
};

/**
 * Format an ISO timestamp or YYYYMMDD date to a friendly display format.
 */
export const formatInquiryDate = (dateStr, format = 'MMM DD, YYYY') => {
  if (!dateStr) return '';
  const m = moment(dateStr);
  if (!m.isValid()) return dateStr;
  return m.format(format);
};

/**
 * Format a createdDt timestamp to "25 Oct 2025 at 2:32pm" style.
 */
export const formatCreatedDate = dateStr => {
  if (!dateStr) return '';
  const m = moment(dateStr);
  if (!m.isValid()) return dateStr;
  return m.format('DD MMM YYYY [at] h:mma');
};
